import FanChartView from '../components/FanChartView'; // Fixed FanChart for large families
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import FlowChartView from '../components/FlowChartView';
import FlowChartView2 from '../components/FlowChartView2';
import FamilyMembersTable from '../components/FamilyMembersTable'; // Optimized table
import SearchPerson from '../components/SearchPerson';
import PersonForm from '../components/PersonForm';
import { Button, Box, Typography, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, CircularProgress, Grid, Card, CardContent, CardActionArea, Paper, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import '../styles.css';
import { getCached, setCached } from '../cache';

const EDITOR_ROLES = ['ADMIN', 'PRESIDENT', 'ELDER'];

const TreePage = ({ currentUser }) => {
  const isEditor = EDITOR_ROLES.includes(currentUser?.role);
  // Tighter than isEditor: only the admin, or someone specifically granted
  // tree-edit rights, may edit/delete an existing person in the table.
  // (Adding a NEW person is unaffected — that still uses isEditor above.)
  const canEditTable = currentUser?.role === 'ADMIN' || currentUser?.canEditTree === true;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const relDeepLinkRan = useRef(false);

  const [people, setPeople] = useState([]);
  // Person lookup map, built once per data load instead of on every
  // relationship computation (and every recursive call)
  const personMapMemo = useMemo(
    () => Object.fromEntries(people.map(p => [p._id, p])),
    [people]
  );
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [view, setView] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personToEdit, setPersonToEdit] = useState(null);
  const [subTreePerson, setSubTreePerson] = useState(null);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [relationshipResult, setRelationshipResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  const fetchPeople = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;

    // Show whatever we fetched last time INSTANTLY, so the tree never opens
    // to a blank spinner while the network call is still in flight — then
    // this same function's fresh result replaces it a moment later.
    if (retryCount === 0) {
      const cached = getCached('people');
      if (cached && cached.data && cached.data.length > 0) {
        setPeople(cached.data);
        setLoading(false);
      } else {
        setLoading(true);
      }
    }

    const apiUrl = process.env.REACT_APP_API_URL || "https://mwendwa-kitavi-family-tree-backend-production.up.railway.app";
    
    if (!apiUrl) {
      console.error('❌ CRITICAL: REACT_APP_API_URL is undefined!');
      console.error('Please create a .env file with REACT_APP_API_URL=your-backend-url');
      console.error('Then restart your development server (npm start)');
      alert('Configuration Error: API URL is not set.\n\nPlease:\n1. Create .env file in your project root\n2. Add: REACT_APP_API_URL=your-backend-url\n3. Restart the development server');
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${apiUrl}/api/people`);
      
      const validPeople = res.data.filter(person => person && person._id && person.name);
      const cleanedPeople = validPeople.map(person => {
        const cleaned = { ...person };
        ['mother', 'father', 'socialParent', 'adoptiveFather'].forEach(field => {
          if (cleaned[field] && !validPeople.some(p => p._id === (cleaned[field]._id || cleaned[field]))) {
            cleaned[field] = null;
          }
        });
        ['spouses', 'kawetos', 'children', 'adoptedSpouses'].forEach(field => {
          if (Array.isArray(cleaned[field])) {
            cleaned[field] = cleaned[field].filter(id => validPeople.some(p => p._id === (id._id || id)));
          }
        });
        return cleaned;
      });
      setPeople(cleanedPeople);
      setCached('people', cleanedPeople); // instant load next time
      setFilteredPeople(cleanedPeople);
    } catch (err) {
      console.error('❌ Error fetching people:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: `${apiUrl}/api/people`,
        fullError: err
      });
      
      if (retryCount < maxRetries) {
        setTimeout(() => fetchPeople(retryCount + 1), 2000);
      } else {
        let errorMsg = 'Failed to fetch people after multiple attempts.';
        
        if (err.response?.status === 404) {
          errorMsg = 'Backend endpoint not found (404).\n\nPlease verify:\n1. Your backend is running\n2. The URL in .env is correct\n3. Your Render backend is awake (not sleeping)';
        } else if (err.message === 'Network Error' || !err.response) {
          errorMsg = 'Cannot connect to backend.\n\nPlease verify:\n1. Backend is running\n2. CORS is configured correctly\n3. URL in .env is correct';
        } else if (err.response?.status === 500) {
          errorMsg = 'Backend server error.\n\nCheck backend logs for details.';
        }
        
        alert(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchPeople();
  }, [fetchPeople]);

  const getAncestorsWithDepth = useCallback((person, personMap) => {
    const ancestors = new Map();
    const queue = [{ person, depth: 0 }];
    const visited = new Set();
    while (queue.length > 0) {
      const { person, depth } = queue.shift();
      if (!person || !person._id || visited.has(person._id)) continue;
      visited.add(person._id);
      ancestors.set(person._id, { person, depth });
      const parents = [];
      ['mother', 'father', 'socialParent'].forEach(field => {
        const parentId = person[field]?._id || person[field];
        if (parentId && personMap[parentId]) parents.push(personMap[parentId]);
      });
      parents.forEach(parent => {
        if (!visited.has(parent._id)) queue.push({ person: parent, depth: depth + 1 });
      });
    }
    return ancestors;
  }, []);

  const getDescendantsWithDepth = useCallback((person, personMap) => {
    const descendants = new Map();
    const queue = [{ person, depth: 0 }];
    const visited = new Set();
    while (queue.length > 0) {
      const { person, depth } = queue.shift();
      if (!person || !person._id || visited.has(person._id)) continue;
      visited.add(person._id);
      descendants.set(person._id, { person, depth });
      const children = (person.children || []).filter(child => child && personMap[child._id]).map(child => personMap[child._id]);
      children.forEach(child => {
        if (!visited.has(child._id)) queue.push({ person: child, depth: depth + 1 });
      });
    }
    return descendants;
  }, []);

  const areSiblings = (person1, person2, personMap) => {
    if (person1._id === person2._id) return false;
    
    // Get all parents (mother, father, socialParent)
    const p1Parents = [person1.mother, person1.father, person1.socialParent]
      .filter(id => id && (id._id || personMap[id]))
      .map(id => id._id || id);
    const p2Parents = [person2.mother, person2.father, person2.socialParent]
      .filter(id => id && (id._id || personMap[id]))
      .map(id => id._id || id);
    
    // Check if they share a direct parent
    const hasSharedParent = p1Parents.some(p1 => p2Parents.includes(p1));
    if (hasSharedParent) return true;
    
    // Check if they're in each other's parent's children arrays
    const p1ParentDocs = p1Parents.map(id => personMap[id]).filter(doc => doc);
    const p2ParentDocs = p2Parents.map(id => personMap[id]).filter(doc => doc);
    
    // Helper to check if a person is in a children array (handles both ObjectId and string IDs)
    const isInChildrenArray = (children, personId) => {
      if (!children || !Array.isArray(children)) return false;
      return children.some(child => {
        const childId = child?._id?.toString() || child?.toString();
        return childId === personId?.toString();
      });
    };
    
    if (p2ParentDocs.some(doc => isInChildrenArray(doc.children, person1._id)) ||
        p1ParentDocs.some(doc => isInChildrenArray(doc.children, person2._id))) {
      return true;
    }
    
    // Check if their parents are Kaweto partners (co-wives/co-husbands)
    // This is the ONLY step-relationship check we do here - using Kaweto directly, not isStepMother
    for (const p1Parent of p1ParentDocs) {
      for (const p2Parent of p2ParentDocs) {
        if (isKawetoPartner(p1Parent, p2Parent)) {
          // They have Kaweto parents!
          // Check if they share the other parent (strict)
          const p1OtherParents = p1ParentDocs.filter(p => p._id !== p1Parent._id);
          const p2OtherParents = p2ParentDocs.filter(p => p._id !== p2Parent._id);
          
          const shareOtherParent = p1OtherParents.some(p1Other => 
            p2OtherParents.some(p2Other => p1Other._id === p2Other._id)
          );
          
          if (shareOtherParent) {
            return true; // They share one parent, and their other parents are Kaweto partners
          }
          
          // If one or both don't have the "other parent" set, still consider them siblings
          // This handles cases where father/mother is not recorded in database
          if (p1OtherParents.length === 0 || p2OtherParents.length === 0) {
            return true; // Kaweto partners' children are siblings even if other parent not recorded
          }
        }
      }
    }
    
    return false;
  };

  const areStepCousins = (person1, person2, personMap) => {
    if (person1._id === person2._id) return false;
    
    // Get parents of both people
    const p1Parents = [person1.mother, person1.father, person1.socialParent]
      .filter(id => id && personMap[id._id || id])
      .map(id => personMap[id._id || id]);
    
    const p2Parents = [person2.mother, person2.father, person2.socialParent]
      .filter(id => id && personMap[id._id || id])
      .map(id => personMap[id._id || id]);
    
    if (p1Parents.length === 0 || p2Parents.length === 0) return false;
    
    
    // CRITICAL FOR KAWETO CHILDREN WITHOUT FATHERS:
    // Check if person1's mother is Kaweto of someone whose husband is person2's father
    // This handles: Agnes (child of Mboka) where Mboka is Kaweto of Ng'amumo, 
    //               and Ng'amumo is wife of Chief Mwendwa who is father of Munguti
    //               Therefore Agnes and Munguti are step-cousins
    
    const p1Mother = person1.mother ? personMap[person1.mother._id || person1.mother] : null;
    const p2Father = person2.father ? personMap[person2.father._id || person2.father] : null;
    
    if (p1Mother && p2Father) {
      
      // Find who p1Mother is Kaweto of
      const kawetoOwner = p1Mother.kawetoOf ? personMap[p1Mother.kawetoOf._id || p1Mother.kawetoOf] : null;
      
      if (kawetoOwner) {
        
        // Check if kawetoOwner's spouse is p2Father
        const kawetoOwnerSpouses = (kawetoOwner.spouses || [])
          .filter(spouse => spouse && personMap[spouse._id])
          .map(spouse => personMap[spouse._id]);
        
        for (const spouse of kawetoOwnerSpouses) {
          if (spouse._id === p2Father._id) {
            return true;
          }
        }
      }
    }
    
    // Reverse check: person2's mother is Kaweto of someone whose husband is person1's father
    const p2Mother = person2.mother ? personMap[person2.mother._id || person2.mother] : null;
    const p1Father = person1.father ? personMap[person1.father._id || person1.father] : null;
    
    if (p2Mother && p1Father) {
      
      const kawetoOwner = p2Mother.kawetoOf ? personMap[p2Mother.kawetoOf._id || p2Mother.kawetoOf] : null;
      
      if (kawetoOwner) {
        
        const kawetoOwnerSpouses = (kawetoOwner.spouses || [])
          .filter(spouse => spouse && personMap[spouse._id])
          .map(spouse => personMap[spouse._id]);
        
        for (const spouse of kawetoOwnerSpouses) {
          if (spouse._id === p1Father._id) {
            return true;
          }
        }
      }
    }
    
    // Check for step-cousins via shared spouses (non-recursive check)
    for (const p1Parent of p1Parents) {
      for (const p2Parent of p2Parents) {
        // Check if p1Parent and p2Parent share a common spouse/partner
        const p1ParentSpouses = (p1Parent.spouses || [])
          .filter(spouse => spouse && personMap[spouse._id])
          .map(spouse => personMap[spouse._id]);
        
        const p2ParentSpouses = (p2Parent.spouses || [])
          .filter(spouse => spouse && personMap[spouse._id])
          .map(spouse => personMap[spouse._id]);
        
        // Check if they share a common spouse
        for (const p1Spouse of p1ParentSpouses) {
          for (const p2Spouse of p2ParentSpouses) {
            if (p1Spouse._id === p2Spouse._id) {
              return true;
            }
          }
        }
        
        // Check if p1Parent is Kaweto partner of p2Parent
        if (isKawetoPartner(p1Parent, p2Parent)) {
          return true;
        }
      }
    }
    
    return false;
  };

  const areCousins = (person1, person2, personMap, maxLevel = 2) => {
    if (person1._id === person2._id) return false;
    const firstAncestors = getAncestorsWithDepth(person1, personMap);
    const secondAncestors = getAncestorsWithDepth(person2, personMap);
    const firstAncestorIds = new Set(firstAncestors.keys());
    let commonAncestorId = null;
    let firstDepth = 0;
    let secondDepth = 0;
    for (const ancestorId of firstAncestorIds) {
      if (secondAncestors.has(ancestorId)) {
        commonAncestorId = ancestorId;
        firstDepth = firstAncestors.get(ancestorId).depth;
        secondDepth = secondAncestors.get(ancestorId).depth;
        break;
      }
    }
    if (!commonAncestorId) return false;
    const genDiff = Math.abs(firstDepth - secondDepth);
    if (genDiff !== 0) return false;
    const cousinLevel = Math.min(firstDepth, secondDepth) - 1;
    return cousinLevel > 0 && cousinLevel <= maxLevel;
  };

  const isParentSibling = (potentialSibling, person, personMap) => {
    // Helper to check if a person is in a children array (handles both ObjectId and string IDs)
    const isInChildrenArray = (children, personId) => {
      if (!children || !Array.isArray(children)) return false;
      return children.some(child => {
        const childId = child?._id?.toString() || child?.toString();
        return childId === personId?.toString();
      });
    };
    
    const personParents = [person.mother, person.father, person.socialParent]
      .filter(id => id && personMap[id._id || id])
      .map(id => personMap[id._id || id]);
    for (const parent of personParents) {
      if (isInChildrenArray(parent.children, potentialSibling._id)) return true;
      const parentParents = [parent.mother, parent.father, parent.socialParent]
        .filter(id => id && personMap[id._id || id])
        .map(id => id._id || id);
      const siblingParents = [potentialSibling.mother, potentialSibling.father, person.socialParent]
        .filter(id => id && personMap[id._id || id])
        .map(id => id._id || id);
      if (parentParents.some(pp => siblingParents.includes(pp))) return true;
      const siblingParentDocs = [potentialSibling.mother, potentialSibling.father, potentialSibling.socialParent]
        .filter(id => id && personMap[id._id || id])
        .map(id => personMap[id._id || id]);
      if (siblingParentDocs.some(doc => isInChildrenArray(doc.children, parent._id))) return true;
    }
    return false;
  };

  const isSpouse = (person1, person2) => {
    const p1Spouses = (person1.spouses || []).map(s => s._id?.toString());
    const p2Spouses = (person2.spouses || []).map(s => s._id?.toString());
    return p1Spouses.includes(person2._id.toString()) || p2Spouses.includes(person1._id.toString());
  };

  const isSisterInLaw = (person1, person2, personMap) => {
    const person2Siblings = people.filter(p => areSiblings(p, person2, personMap));
    const person2Cousins = people.filter(p => areCousins(p, person2, personMap, 2));
    const isPerson1SpouseOfSiblingOrCousin = [...person2Siblings, ...person2Cousins].some(relative => isSpouse(person1, relative));
    const person1Siblings = people.filter(p => areSiblings(p, person1, personMap));
    const person1Cousins = people.filter(p => areCousins(p, person1, personMap, 2));
    const isPerson2SpouseOfSiblingOrCousin = [...person1Siblings, ...person1Cousins].some(relative => isSpouse(person2, relative));
    return (isPerson1SpouseOfSiblingOrCousin || isPerson2SpouseOfSiblingOrCousin) && person1.gender === 'FEMALE';
  };

  const isBrotherInLaw = (person1, person2, personMap) => {
    const person2Siblings = people.filter(p => areSiblings(p, person2, personMap));
    const person2Cousins = people.filter(p => areCousins(p, person2, personMap, 2));
    const isPerson1SpouseOfSiblingOrCousin = [...person2Siblings, ...person2Cousins].some(relative => isSpouse(person1, relative));
    const person1Siblings = people.filter(p => areSiblings(p, person1, personMap));
    const person1Cousins = people.filter(p => areCousins(p, person1, personMap, 2));
    const isPerson2SpouseOfSiblingOrCousin = [...person1Siblings, ...person1Cousins].some(relative => isSpouse(person2, relative));
    return (isPerson1SpouseOfSiblingOrCousin || isPerson2SpouseOfSiblingOrCousin) && person1.gender === 'MALE';
  };

  const isParentInLaw = (person1, person2, personMap) => {
    const person2Spouses = (person2.spouses || []).filter(spouse => spouse && personMap[spouse._id]).map(spouse => personMap[spouse._id]);
    return person2Spouses.some(spouse => {
      const spouseParents = [spouse.mother, spouse.father, spouse.socialParent]
        .filter(id => id && personMap[id._id || id])
        .map(id => personMap[id._id || id]);
      return spouseParents.some(parent => parent._id === person1._id);
    });
  };

  const getGreatPrefix = genDiff => genDiff <= 2 ? '' : 'GREAT_'.repeat(genDiff - 2);

  const isKawetoPartner = (person1, person2) => {
    const p1Kawetos = person1.kawetos || [];
    const p2Kawetos = person2.kawetos || [];
    return p1Kawetos.some(kaweto => kaweto?._id?.toString() === person2._id.toString()) ||
           p2Kawetos.some(kaweto => kaweto?._id?.toString() === person1._id.toString());
  };

  const isAuntByMarriage = (person1, person2, personMap) => {
    if (person1.gender !== 'FEMALE') return false;
    const person2Parents = [person2.mother, person2.father, person2.socialParent]
      .filter(id => id && personMap[id._id || id])
      .map(id => personMap[id._id || id]);
    const uncles = people.filter(p => 
      person2Parents.some(parent => areSiblings(p, parent, personMap) && p.gender === 'MALE')
    );
    return uncles.some(uncle => isSpouse(person1, uncle));
  };

  const isChildOfParent = (child, parent, personMap) => {
    return [child.mother, child.father, child.socialParent]
      .filter(id => id && personMap[id._id || id])
      .map(id => personMap[id._id || id])
      .some(p => p._id === parent._id);
  };

  const isStepMother = (person1, person2, personMap) => {
    if (person1.gender !== 'FEMALE') return false;
    
    // Get person2's parents
    const secondPersonFather = person2.father && personMap[person2.father._id || person2.father];
    const secondPersonMother = person2.mother && personMap[person2.mother._id || person2.mother];
    const secondPersonSocialParent = person2.socialParent && personMap[person2.socialParent._id || person2.socialParent];
    
    
    // Check if person1 is NOT person2's biological mother
    if (secondPersonMother && secondPersonMother._id === person1._id) {
      return false;
    }
    
    // Check if person1 is spouse/kaweto of person2's FATHER
    if (secondPersonFather) {
      const isSpouseCheck = isSpouse(person1, secondPersonFather);
      const isKawetoCheck = isKawetoPartner(person1, secondPersonFather);
      
      if (isSpouseCheck || isKawetoCheck) {
        return true;
      }
    }
    
    // NEW: Check if person1 is KAWETO of person2's MOTHER (co-wife scenario)
    if (secondPersonMother) {
      const isKawetoCheck = isKawetoPartner(person1, secondPersonMother);
      
      if (isKawetoCheck) {
        return true;
      }
    }
    
    // Check socialParent
    if (secondPersonSocialParent) {
      const isSpouseCheck = isSpouse(person1, secondPersonSocialParent);
      const isKawetoCheck = isKawetoPartner(person1, secondPersonSocialParent);
      
      if (isSpouseCheck || isKawetoCheck) {
        return true;
      }
    }
    
    // CRITICAL NEW LOGIC: Check if person1 is Kaweto of ANY spouse of person2's father
    // This handles: Mboka is Kaweto of Ng'amumo → Ng'amumo is wife of Chief Mwendwa → Mboka is stepmother to ALL Chief Mwendwa's children
    if (secondPersonFather) {
      const fatherSpouses = (secondPersonFather.spouses || [])
        .filter(spouse => spouse && personMap[spouse._id])
        .map(spouse => personMap[spouse._id]);
      
      
      for (const spouse of fatherSpouses) {
        const isKawetoOfSpouse = isKawetoPartner(person1, spouse);
        
        if (isKawetoOfSpouse) {
          return true;
        }
      }
    }
    
    // Also check if person1 is Kaweto of ANY spouse of person2's mother's husband
    // This handles polygamous families where mother has co-wives
    if (secondPersonMother) {
      const motherSpouses = (secondPersonMother.spouses || [])
        .filter(spouse => spouse && personMap[spouse._id])
        .map(spouse => personMap[spouse._id]);
      
      for (const motherSpouse of motherSpouses) {
        // Get all spouses of this person (co-wives)
        const coWives = (motherSpouse.spouses || [])
          .filter(spouse => spouse && personMap[spouse._id])
          .map(spouse => personMap[spouse._id]);
        
        for (const coWife of coWives) {
          // Check if person1 is Kaweto of this co-wife
          const isKawetoOfCoWife = isKawetoPartner(person1, coWife);
          
          if (isKawetoOfCoWife) {
            return true;
          }
        }
      }
    }
    
    return false;
  };

  const isStepFather = (person1, person2, personMap) => {
    if (person1.gender !== 'MALE') return false;
    
    // Get person2's parents (mother and socialParent, not father)
    const secondPersonParents = [person2.mother, person2.socialParent]
      .filter(id => id && personMap[id._id || id])
      .map(id => personMap[id._id || id]);
    
    const secondPersonFather = person2.father && personMap[person2.father._id || person2.father];
    
    // Check if person1 is spouse OR kaweto partner of person2's mother/socialParent
    // AND person1 is NOT person2's biological father
    return secondPersonParents.some(parent => 
      (isSpouse(person1, parent) || isKawetoPartner(person1, parent)) && 
      (!secondPersonFather || secondPersonFather._id !== person1._id)
    );
  };

  // NEW: Check if person1 is step-grandmother of person2 (stepmother of person2's parent)
  const isStepGrandmother = (person1, person2, personMap) => {
    if (person1.gender !== 'FEMALE') return false;
    const person2Parents = [person2.mother, person2.father, person2.socialParent]
      .filter(id => id && personMap[id._id || id])
      .map(id => personMap[id._id || id]);
    return person2Parents.some(parent => isStepMother(person1, parent, personMap));
  };

  const isGrandparentByMarriage = (person1, person2, personMap) => {
    const secondAncestors = getAncestorsWithDepth(person2, personMap);
    const person1Spouses = (person1.spouses || [])
      .filter(spouse => spouse && personMap[spouse._id])
      .map(spouse => personMap[spouse._id]);
    return person1Spouses.some(spouse => {
      if (!secondAncestors.has(spouse._id)) return false;
      const genDiff = secondAncestors.get(spouse._id).depth;
      if (genDiff < 2) return false;
      const secondParents = [person2.mother, person2.father, person2.socialParent]
        .filter(id => id && personMap[id._id || id])
        .map(id => personMap[id._id || id]);
      return !secondParents.some(parent => parent._id === person1._id);
    }) && secondAncestors.size > 0;
  };

  const getGrandparentGeneration = (person1, person2, personMap) => {
    const secondAncestors = getAncestorsWithDepth(person2, personMap);
    const person1Spouses = (person1.spouses || [])
      .filter(spouse => spouse && personMap[spouse._id])
      .map(spouse => personMap[spouse._id]);
    for (const spouse of person1Spouses) {
      if (secondAncestors.has(spouse._id)) {
        return secondAncestors.get(spouse._id).depth;
      }
    }
    return 0;
  };

  const handleRelationship = ({ firstPersonId, secondPersonId }, depth = 0) => {
    
    // CRITICAL: Prevent infinite recursion by limiting depth
    if (depth > 5) {
      return { 
        firstPerson: 'Unknown', 
        secondPerson: 'Unknown', 
        relationship: 'NO_RELATION',
        isSwapped: false 
      };
    }
    
    if (!firstPersonId || !secondPersonId) {
      setRelationshipResult({ firstPerson: 'Unknown', secondPerson: 'Unknown', relationship: 'INVALID_INPUT' });
      return;
    }
    if (loading || !people.length) {
      setRelationshipResult({ firstPerson: 'Unknown', secondPerson: 'Unknown', relationship: 'LOADING' });
      return;
    }

    const personMap = personMapMemo;
    const firstPerson = personMap[firstPersonId];
    const secondPerson = personMap[secondPersonId];

    if (!firstPerson || !secondPerson) {
      setRelationshipResult({
        firstPerson: firstPerson?.name || 'Unknown',
        secondPerson: secondPerson?.name || 'Unknown',
        relationship: 'NO_RELATION'
      });
      return;
    }

    let relationship = 'NO_RELATION';
    let isSwapped = false;

    if (firstPerson._id === secondPerson._id) {
      relationship = 'SELF';
    } else {
      // FIRST: Check for DIRECT ancestor/descendant relationships BEFORE step relationships
      const firstAncestors = getAncestorsWithDepth(firstPerson, personMap);
      const secondAncestors = getAncestorsWithDepth(secondPerson, personMap);
      const firstDescendants = getDescendantsWithDepth(firstPerson, personMap);
      const secondDescendants = getDescendantsWithDepth(secondPerson, personMap);

      // Check direct ancestor relationships first
      if (secondAncestors.has(firstPerson._id)) {
        const genDiff = secondAncestors.get(firstPerson._id).depth;
        if (genDiff === 1) {
          relationship = 'PARENT';
        } else if (genDiff === 2) {
          relationship = 'GRANDPARENT';
        } else if (genDiff > 2) {
          relationship = `${getGreatPrefix(genDiff)}GRANDPARENT`;
        }
      } else if (firstAncestors.has(secondPerson._id)) {
        const genDiff = firstAncestors.get(secondPerson._id).depth;
        if (genDiff === 1) {
          relationship = 'PARENT';
          isSwapped = true;
        } else if (genDiff === 2) {
          relationship = 'GRANDPARENT';
          isSwapped = true;
        } else if (genDiff > 2) {
          relationship = `${getGreatPrefix(genDiff)}GRANDPARENT`;
          isSwapped = true;
        }
      } else if (firstDescendants.has(secondPerson._id)) {
        const genDiff = firstDescendants.get(secondPerson._id).depth;
        if (genDiff === 1) {
          relationship = 'CHILD';
        } else if (genDiff === 2) {
          relationship = 'GRANDCHILD';
        } else if (genDiff > 2) {
          relationship = `${getGreatPrefix(genDiff)}GRANDCHILD`;
        }
      } else if (secondDescendants.has(firstPerson._id)) {
        const genDiff = secondDescendants.get(firstPerson._id).depth;
        if (genDiff === 1) {
          relationship = 'CHILD';
          isSwapped = true;
        } else if (genDiff === 2) {
          relationship = 'GRANDCHILD';
          isSwapped = true;
        } else if (genDiff > 2) {
          relationship = `${getGreatPrefix(genDiff)}GRANDCHILD`;
          isSwapped = true;
        }
      }
      
      // If direct ancestor relationship found, return it immediately
      if (relationship !== 'NO_RELATION') {
        const result = {
          firstPerson: isSwapped ? secondPerson.name : firstPerson.name,
          secondPerson: isSwapped ? firstPerson.name : secondPerson.name,
          relationship,
          isSwapped
        };
        setRelationshipResult(result);
        return result;
      }
      
      // SECOND: Check for other relationships (step, siblings, cousins, etc.)
      if (isStepMother(firstPerson, secondPerson, personMap)) {
        relationship = 'STEPMOTHER';
      } else if (isStepFather(firstPerson, secondPerson, personMap)) {
        relationship = 'STEPFATHER';
      } else if (isStepMother(secondPerson, firstPerson, personMap)) {
        relationship = 'STEPMOTHER';
        isSwapped = true;
      } else if (isStepFather(secondPerson, firstPerson, personMap)) {
        relationship = 'STEPFATHER';
        isSwapped = true;
      } else if (isStepGrandmother(firstPerson, secondPerson, personMap)) {
        // NEW: Step-grandmother via Kaweto (e.g., Mboka → Mumo)
        relationship = 'GRANDPARENT';
      } else if (isStepGrandmother(secondPerson, firstPerson, personMap)) {
        relationship = 'GRANDPARENT';
        isSwapped = true;
      } else if (areSiblings(firstPerson, secondPerson, personMap)) {
        // Check if they are siblings (includes biological, step-siblings via Kaweto, etc.)
        relationship = 'SIBLINGS';
      }
      
      // NEW: STEPPARENT → AUNT/UNCLE CASCADE
      // If Person A is stepparent of Person B, then Person A is aunt/uncle to all of Person B's siblings' children
      // Example: Mboka is stepmother to Kyale → Mboka is aunt to children of Kyale's siblings (like Munguti)
      if (relationship === 'NO_RELATION') {
        // Get secondPerson's parents
        const secondParents = [secondPerson.mother, secondPerson.father, secondPerson.socialParent]
          .filter(id => id && personMap[id._id || id])
          .map(id => personMap[id._id || id]);
        
        // Check if firstPerson is stepparent to any of secondPerson's parent's siblings
        for (const parent of secondParents) {
          // Get all siblings of this parent
          const parentSiblings = people.filter(p => 
            p._id !== parent._id && areSiblings(p, parent, personMap)
          );
          
          // Check if firstPerson is stepparent to any of these siblings
          for (const sibling of parentSiblings) {
            if (isStepMother(firstPerson, sibling, personMap)) {
              relationship = 'AUNT';
              break;
            } else if (isStepFather(firstPerson, sibling, personMap)) {
              relationship = 'UNCLE';
              break;
            }
          }
          
          if (relationship !== 'NO_RELATION') break;
        }
      }
      
      // Check swapped: secondPerson is stepparent to firstPerson's parent's sibling
      if (relationship === 'NO_RELATION') {
        const firstParents = [firstPerson.mother, firstPerson.father, firstPerson.socialParent]
          .filter(id => id && personMap[id._id || id])
          .map(id => personMap[id._id || id]);
        
        for (const parent of firstParents) {
          const parentSiblings = people.filter(p => 
            p._id !== parent._id && areSiblings(p, parent, personMap)
          );
          
          for (const sibling of parentSiblings) {
            if (isStepMother(secondPerson, sibling, personMap)) {
              relationship = 'AUNT';
              isSwapped = true;
              break;
            } else if (isStepFather(secondPerson, sibling, personMap)) {
              relationship = 'UNCLE';
              isSwapped = true;
              break;
            }
          }
          
          if (relationship !== 'NO_RELATION') break;
        }
      }
      
      // NEW: Check for step-uncle/aunt relationships
      // If person1's stepparent's child → person2, then person1 is uncle/aunt of person2
      if (relationship === 'NO_RELATION') {
          const secondParents = [secondPerson.mother, secondPerson.father, secondPerson.socialParent]
            .filter(id => id && personMap[id._id || id])
            .map(id => personMap[id._id || id]);
          
          // FIRST: Check if firstPerson's parent is stepparent of secondPerson's parent (DIRECT CHECK - NO RECURSION!)
          const firstParentDocs = [firstPerson.mother, firstPerson.father, firstPerson.socialParent]
            .filter(id => id && personMap[id._id || id])
            .map(id => personMap[id._id || id]);
          
          for (const parent of secondParents) {
            // Direct step-sibling check: Is firstPerson's parent a stepparent of this parent?
            for (const firstParent of firstParentDocs) {
              if (isStepMother(firstParent, parent, personMap) || isStepFather(firstParent, parent, personMap)) {
                relationship = firstPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                break;
              }
            }
            
            if (relationship !== 'NO_RELATION') break;
            
            // SECOND: Check via areSiblings (for other sibling types like shared parents, Kaweto, etc.)
            if (areSiblings(firstPerson, parent, personMap)) {
              relationship = firstPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
              break;
            }
            
            // Check if firstPerson is child of parent's stepparent
            const parentStepParents = [];
            const grandparents = [parent.mother, parent.father, parent.socialParent]
              .filter(id => id && personMap[id._id || id])
              .map(id => personMap[id._id || id]);
            
            for (const grandparent of grandparents) {
              if (isStepMother(grandparent, parent, personMap) || isStepFather(grandparent, parent, personMap)) {
                parentStepParents.push(grandparent);
              }
            }
            
            // Check if firstPerson is child of any of parent's stepparents
            for (const stepParent of parentStepParents) {
              const firstParents = [firstPerson.mother, firstPerson.father, firstPerson.socialParent]
                .filter(id => id && personMap[id._id || id])
                .map(id => id._id || id);
              
              if (firstParents.includes(stepParent._id)) {
                relationship = firstPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                break;
              }
            }
            
            if (relationship !== 'NO_RELATION') break;
          }
        }
        
        // Check swapped (secondPerson is uncle/aunt of firstPerson)
        if (relationship === 'NO_RELATION') {
          const firstParents = [firstPerson.mother, firstPerson.father, firstPerson.socialParent]
            .filter(id => id && personMap[id._id || id])
            .map(id => personMap[id._id || id]);
          
          for (const parent of firstParents) {
            if (areSiblings(secondPerson, parent, personMap)) {
              relationship = secondPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
              isSwapped = true;
              break;
            }
            
            // Check step-uncle/aunt swapped
            const parentStepParents = [];
            const grandparents = [parent.mother, parent.father, parent.socialParent]
              .filter(id => id && personMap[id._id || id])
              .map(id => personMap[id._id || id]);
            
            for (const grandparent of grandparents) {
              if (isStepMother(grandparent, parent, personMap) || isStepFather(grandparent, parent, personMap)) {
                parentStepParents.push(grandparent);
              }
            }
            
            for (const stepParent of parentStepParents) {
              const secondParents = [secondPerson.mother, secondPerson.father, secondPerson.socialParent]
                .filter(id => id && personMap[id._id || id])
                .map(id => id._id || id);
              
              if (secondParents.includes(stepParent._id)) {
                relationship = secondPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                isSwapped = true;
                break;
              }
            }
            
            if (relationship !== 'NO_RELATION') break;
          }
        }
      
      // Check for cousin relationships
      // Cousins are people whose parents are siblings OR whose parent is aunt/uncle of the other person
      if (relationship === 'NO_RELATION') {
        // First check via areCousins (parents are siblings)
        if (areCousins(firstPerson, secondPerson, personMap, 2)) {
          relationship = 'COUSIN';
        }
        
        // NEW: Check for step-cousins
        // Step-cousins are children whose parents are stepmothers/stepfathers in the same family
        if (relationship === 'NO_RELATION' && areStepCousins(firstPerson, secondPerson, personMap)) {
          relationship = 'COUSIN';
        }
        
        // Also check if firstPerson's parent is aunt/uncle of secondPerson
        if (relationship === 'NO_RELATION') {
          const firstParentDocs = [firstPerson.father, firstPerson.mother, firstPerson.socialParent]
            .filter(parent => parent && personMap[parent._id])
            .map(parent => personMap[parent._id]);
          
          for (const parent of firstParentDocs) {
            // Check if this parent is aunt/uncle of secondPerson
            const parentSiblings = people.filter(p => 
              p._id !== parent._id && areSiblings(p, parent, personMap)
            );
            
            const secondParentDocs = [secondPerson.father, secondPerson.mother, secondPerson.socialParent]
              .filter(p => p && personMap[p._id])
              .map(p => personMap[p._id]);
            
            // If parent is sibling of secondPerson's parent, they're cousins
            for (const secondParent of secondParentDocs) {
              if (parentSiblings.some(s => s._id === secondParent._id)) {
                relationship = 'COUSIN';
                break;
              }
            }
            
            if (relationship !== 'NO_RELATION') break;
          }
        }
      }
      
      if (relationship === 'NO_RELATION' && firstPerson.gender === 'FEMALE' && depth < 2) {
        const firstSpouses = (firstPerson.spouses || [])
          .filter(spouse => spouse && personMap[spouse._id])
          .map(spouse => personMap[spouse._id]);
        const secondParents = [secondPerson.mother, secondPerson.father, secondPerson.socialParent]
          .filter(id => id && personMap[id._id || id])
          .map(id => personMap[id._id || id]);
        for (const spouse of firstSpouses) {
          const spouseToSecond = handleRelationship({ firstPersonId: spouse._id, secondPersonId: secondPerson._id }, depth + 1);
          if (spouseToSecond?.relationship === 'NEPHEW' || spouseToSecond?.relationship === 'NIECE') {
            relationship = 'AUNT';
            setRelationshipResult({
              firstPerson: firstPerson.name,
              secondPerson: secondPerson.name,
              relationship,
              isSwapped: false
            });
            return;
          }
          for (const parent of secondParents) {
            if (
              isBrotherInLaw(spouse, parent, personMap) &&
              isChildOfParent(secondPerson, parent, personMap)
            ) {
              relationship = 'AUNT';
              setRelationshipResult({
                firstPerson: firstPerson.name,
                secondPerson: secondPerson.name,
                relationship,
                isSwapped: false
              });
              return;
            }
          }
        }
      }
      if (relationship === 'NO_RELATION' && secondPerson.gender === 'FEMALE' && depth < 2) {
        const secondSpouses = (secondPerson.spouses || [])
          .filter(spouse => spouse && personMap[spouse._id])
          .map(spouse => personMap[spouse._id]);
        const firstParents = [firstPerson.mother, firstPerson.father, firstPerson.socialParent]
          .filter(id => id && personMap[id._id || id])
          .map(id => personMap[id._id || id]);
        for (const spouse of secondSpouses) {
          const spouseToFirst = handleRelationship({ firstPersonId: spouse._id, secondPersonId: firstPerson._id }, depth + 1);
          if (spouseToFirst?.relationship === 'NEPHEW' || spouseToFirst?.relationship === 'NIECE') {
            relationship = 'AUNT';
            isSwapped = true;
            setRelationshipResult({
              firstPerson: secondPerson.name,
              secondPerson: firstPerson.name,
              relationship,
              isSwapped: true
            });
            return;
          }
          for (const parent of firstParents) {
            if (
              isBrotherInLaw(spouse, parent, personMap) &&
              isChildOfParent(firstPerson, parent, personMap)
            ) {
              relationship = 'AUNT';
              isSwapped = true;
              setRelationshipResult({
                firstPerson: secondPerson.name,
                secondPerson: firstPerson.name,
                relationship,
                isSwapped: true
              });
              return;
            }
          }
        }
      }

      if (relationship === 'NO_RELATION') {
        if (isParentInLaw(firstPerson, secondPerson, personMap)) {
          relationship = firstPerson.gender === 'MALE' ? 'FATHER_IN_LAW' : 'MOTHER_IN_LAW';
        } else if (isParentInLaw(secondPerson, firstPerson, personMap)) {
          relationship = secondPerson.gender === 'MALE' ? 'FATHER_IN_LAW' : 'MOTHER_IN_LAW';
          isSwapped = true;
        } else if (isSisterInLaw(firstPerson, secondPerson, personMap)) {
          relationship = 'SISTER_IN_LAW';
        } else if (isSisterInLaw(secondPerson, firstPerson, personMap)) {
          relationship = 'SISTER_IN_LAW';
          isSwapped = true;
        } else if (isBrotherInLaw(firstPerson, secondPerson, personMap)) {
          relationship = 'BROTHER_IN_LAW';
        } else if (isBrotherInLaw(secondPerson, firstPerson, personMap)) {
          relationship = 'BROTHER_IN_LAW';
          isSwapped = true;
        } else if (isAuntByMarriage(firstPerson, secondPerson, personMap)) {
          relationship = 'AUNT';
        } else if (isAuntByMarriage(secondPerson, firstPerson, personMap)) {
          relationship = 'AUNT';
          isSwapped = true;
        }
      }

      if (relationship === 'NO_RELATION') {
        // Generalized Kamba in-law rules (previously hardcoded for specific
        // named people — now they apply to the whole family):
        //  1) The spouse of a parent's sibling is an aunt/uncle by marriage.
        //  2) A cousin of a parent is culturally an uncle/aunt.
        const parentsOf = (p) => [p.mother, p.father, p.socialParent]
          .filter((ref) => ref && personMap[ref._id || ref])
          .map((ref) => personMap[ref._id || ref]);

        const isSpouseOfParentsSibling = (a, b) => {
          const aSpouses = (a.spouses || [])
            .map((sp) => personMap[sp._id || sp])
            .filter(Boolean);
          if (aSpouses.length === 0) return false;
          return parentsOf(b).some((parent) =>
            aSpouses.some((s) => s._id !== parent._id && areSiblings(s, parent, personMap))
          );
        };

        const isCousinOfParent = (a, b) =>
          parentsOf(b).some((parent) => areCousins(a, parent, personMap));

        if (isSpouseOfParentsSibling(firstPerson, secondPerson)) {
          relationship = firstPerson.gender === 'FEMALE' ? 'AUNT' : 'UNCLE';
        } else if (isSpouseOfParentsSibling(secondPerson, firstPerson)) {
          relationship = secondPerson.gender === 'FEMALE' ? 'AUNT' : 'UNCLE';
          isSwapped = true;
        } else if (isCousinOfParent(firstPerson, secondPerson)) {
          relationship = firstPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
        } else if (isCousinOfParent(secondPerson, firstPerson)) {
          relationship = secondPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
          isSwapped = true;
        }
      }

      if (relationship === 'NO_RELATION') {
        if (isSpouse(firstPerson, secondPerson)) {
          relationship = 'SPOUSE';
        } else if (isKawetoPartner(firstPerson, secondPerson)) {
          relationship = 'KAWETO';
        } else {
          // ===== KAWETO CASCADE CHECKS - Moved here to run AFTER in-law checks =====
          // GENERATIONAL CASCADE: If Person A is COUSIN of Person B's parent → Person A is AUNT/UNCLE of Person B
          // Example: Lucy Agnes (cousin of Mumo) → Aunt of Kyale (Mumo's son)
          // Example: Munguti (step-cousin of Agnes via Kaweto) → Uncle of Lucy Agnes (Agnes's daughter)
          if (relationship === 'NO_RELATION') {
            const secondParentDocs = [secondPerson.father, secondPerson.mother, secondPerson.socialParent]
              .filter(parent => parent && personMap[parent._id])
              .map(parent => personMap[parent._id]);
            
            const firstParentDocs = [firstPerson.father, firstPerson.mother, firstPerson.socialParent]
              .filter(parent => parent && personMap[parent._id])
              .map(parent => personMap[parent._id]);
            
            for (const secondParent of secondParentDocs) {
              // NEW LOGIC: Check if ANY of firstPerson's parents are step-cousins with secondParent
              // If parents are step-cousins (or cousins), then children are COUSINS (second cousins)
              for (const firstParent of firstParentDocs) {
                if (areStepCousins(firstParent, secondParent, personMap)) {
                  relationship = 'COUSIN';
                  break;
                }
              }
              
              if (relationship !== 'NO_RELATION') break;
              
              // ORIGINAL LOGIC: Also check if firstPerson is STEP-COUSIN of secondParent (via Kaweto path or regular step-cousin)
              if (areStepCousins(firstPerson, secondParent, personMap)) {
                relationship = firstPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                break;
              }
              
              // NEW: Direct cousin check - if firstPerson is COUSIN of secondParent, then AUNT/UNCLE of secondPerson
              // ALSO: Great-Aunt/Uncle cascade - if firstPerson is AUNT/UNCLE of secondParent, then GREAT-AUNT/UNCLE of secondPerson
              // Example: Lucy is cousin of Josphat → Lucy is aunt of Robert (Josphat's son)
              // Example: Lucy is aunt of Robert → Lucy is great-aunt of Elisa (Robert's daughter)
              const firstSecondParentRelationship = handleRelationship(
                { firstPersonId: firstPerson._id, secondPersonId: secondParent._id },
                depth + 1
              );
              
              if (firstSecondParentRelationship && firstSecondParentRelationship.relationship === 'COUSIN') {
                relationship = firstPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                break;
              }
              
              if (firstSecondParentRelationship && 
                  (firstSecondParentRelationship.relationship === 'AUNT' || 
                   firstSecondParentRelationship.relationship === 'UNCLE')) {
                relationship = firstPerson.gender === 'MALE' ? 'GREAT_UNCLE' : 'GREAT_AUNT';
                break;
              }
              
              if (firstSecondParentRelationship && 
                  (firstSecondParentRelationship.relationship === 'GREAT_AUNT' || 
                   firstSecondParentRelationship.relationship === 'GREAT_UNCLE')) {
                relationship = firstPerson.gender === 'MALE' ? 'GREAT_GREAT_UNCLE' : 'GREAT_GREAT_AUNT';
                break;
              }
              
              // Check if firstPerson is cousin of this parent (via sibling parents)
              const firstParentDocs_inner = [firstPerson.father, firstPerson.mother, firstPerson.socialParent]
                .filter(p => p && personMap[p._id])
                .map(p => personMap[p._id]);
              
              for (const firstParent_inner of firstParentDocs_inner) {
                // Are the parents siblings? (makes firstPerson and parent cousins)
                const firstParentSiblings = people.filter(p => 
                  p._id !== firstParent_inner._id && areSiblings(p, firstParent_inner, personMap)
                );
                
                const parentParentDocs = [secondParent.father, secondParent.mother, secondParent.socialParent]
                  .filter(p => p && personMap[p._id])
                  .map(p => personMap[p._id]);
                
                for (const parentParent of parentParentDocs) {
                  if (firstParentSiblings.some(s => s._id === parentParent._id)) {
                    relationship = firstPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                    break;
                  }
                }
                
                if (relationship !== 'NO_RELATION') break;
              }
              
              if (relationship !== 'NO_RELATION') break;
            }
          }
          
          // SWAPPED GENERATIONAL CASCADE: If Person B is COUSIN of Person A's parent → Person B is AUNT/UNCLE of Person A
          // Example: Lucy Agnes → Munguti (Munguti is step-cousin of Lucy's parent Agnes)
          if (relationship === 'NO_RELATION') {
            const firstParentDocs = [firstPerson.father, firstPerson.mother, firstPerson.socialParent]
              .filter(parent => parent && personMap[parent._id])
              .map(parent => personMap[parent._id]);
            
            const secondParentDocs = [secondPerson.father, secondPerson.mother, secondPerson.socialParent]
              .filter(parent => parent && personMap[parent._id])
              .map(parent => personMap[parent._id]);
            
            for (const firstParent of firstParentDocs) {
              // NEW LOGIC: Check if ANY of secondPerson's parents are step-cousins with firstParent
              // If parents are step-cousins (or cousins), then children are COUSINS (second cousins)
              for (const secondParent of secondParentDocs) {
                if (areStepCousins(secondParent, firstParent, personMap)) {
                  relationship = 'COUSIN';
                  isSwapped = false; // Not swapped since both are cousins
                  break;
                }
              }
              
              if (relationship !== 'NO_RELATION') break;
              
              // ORIGINAL LOGIC: Also check if secondPerson is STEP-COUSIN of firstParent (via Kaweto path or regular step-cousin)
              if (areStepCousins(secondPerson, firstParent, personMap)) {
                relationship = secondPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                isSwapped = true;
                break;
              }
              
              // NEW: Direct cousin check (swapped) - if secondPerson is COUSIN of firstParent, then AUNT/UNCLE of firstPerson
              // ALSO: Great-Aunt/Uncle cascade (swapped) - if secondPerson is AUNT/UNCLE of firstParent, then GREAT-AUNT/UNCLE of firstPerson
              const secondFirstParentRelationship = handleRelationship(
                { firstPersonId: secondPerson._id, secondPersonId: firstParent._id },
                depth + 1
              );
              
              if (secondFirstParentRelationship && secondFirstParentRelationship.relationship === 'COUSIN') {
                relationship = secondPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                isSwapped = true;
                break;
              }
              
              if (secondFirstParentRelationship && 
                  (secondFirstParentRelationship.relationship === 'AUNT' || 
                   secondFirstParentRelationship.relationship === 'UNCLE')) {
                relationship = secondPerson.gender === 'MALE' ? 'GREAT_UNCLE' : 'GREAT_AUNT';
                isSwapped = true;
                break;
              }
              
              if (secondFirstParentRelationship && 
                  (secondFirstParentRelationship.relationship === 'GREAT_AUNT' || 
                   secondFirstParentRelationship.relationship === 'GREAT_UNCLE')) {
                relationship = secondPerson.gender === 'MALE' ? 'GREAT_GREAT_UNCLE' : 'GREAT_GREAT_AUNT';
                isSwapped = true;
                break;
              }
              
              // Check if secondPerson is cousin of this parent
              const secondParentDocs_inner = [secondPerson.father, secondPerson.mother, secondPerson.socialParent]
                .filter(p => p && personMap[p._id])
                .map(p => personMap[p._id]);
              
              for (const secondParent_inner of secondParentDocs_inner) {
                // Are the parents siblings? (makes secondPerson and parent cousins)
                const secondParentSiblings = people.filter(p => 
                  p._id !== secondParent_inner._id && areSiblings(p, secondParent_inner, personMap)
                );
                
                const parentParentDocs = [firstParent.father, firstParent.mother, firstParent.socialParent]
                  .filter(p => p && personMap[p._id])
                  .map(p => personMap[p._id]);
                
                for (const parentParent of parentParentDocs) {
                  if (secondParentSiblings.some(s => s._id === parentParent._id)) {
                    relationship = secondPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                    isSwapped = true;
                    break;
                  }
                }
                
                if (relationship !== 'NO_RELATION') break;
              }
              
              if (relationship !== 'NO_RELATION') break;
            }
          }
          
          if (relationship === 'NO_RELATION' && isGrandparentByMarriage(firstPerson, secondPerson, personMap)) {
            const genDiff = getGrandparentGeneration(firstPerson, secondPerson, personMap);
            relationship = genDiff === 2 ? 'GRANDPARENT' : `${getGreatPrefix(genDiff)}GRANDPARENT`;
          } else if (relationship === 'NO_RELATION' && isGrandparentByMarriage(secondPerson, firstPerson, personMap)) {
            const genDiff = getGrandparentGeneration(secondPerson, firstPerson, personMap);
            relationship = genDiff === 2 ? 'GRANDPARENT' : `${getGreatPrefix(genDiff)}GRANDPARENT`;
            isSwapped = true;
          }
    
          // ===== END KAWETO CASCADE CHECKS =====

          const firstAncestors = getAncestorsWithDepth(firstPerson, personMap);
          const secondAncestors = getAncestorsWithDepth(secondPerson, personMap);
          const firstDescendants = getDescendantsWithDepth(firstPerson, personMap);
          const secondDescendants = getDescendantsWithDepth(secondPerson, personMap);

          // Enhanced debugging for relationship detection
          
          if (secondAncestors.has(firstPerson._id)) {
            // firstPerson is an ancestor of secondPerson
            // This means: firstPerson is PARENT/GRANDPARENT of secondPerson
            const genDiff = secondAncestors.get(firstPerson._id).depth;
            
            if (genDiff === 1 && !isKawetoPartner(firstPerson, secondPerson)) {
              relationship = 'PARENT';
            } else if (genDiff === 2) {
              relationship = 'GRANDPARENT';
            } else if (genDiff > 2) {
              relationship = `${getGreatPrefix(genDiff)}GRANDPARENT`;
            }
          } else if (firstAncestors.has(secondPerson._id)) {
            // secondPerson is an ancestor of firstPerson
            // This means: secondPerson is PARENT/GRANDPARENT of firstPerson
            // We SWAP to display: secondPerson is GRANDPARENT of firstPerson
            const genDiff = firstAncestors.get(secondPerson._id).depth;
            
            if (genDiff === 1 && !isKawetoPartner(secondPerson, firstPerson)) {
              relationship = 'PARENT';
              isSwapped = true;
            } else if (genDiff === 2) {
              relationship = 'GRANDPARENT';
              isSwapped = true;
            } else if (genDiff > 2) {
              relationship = `${getGreatPrefix(genDiff)}GRANDPARENT`;
              isSwapped = true;
            }
          } else if (firstDescendants.has(secondPerson._id)) {
            // secondPerson is a descendant of firstPerson
            // This means: secondPerson is CHILD/GRANDCHILD of firstPerson
            const genDiff = firstDescendants.get(secondPerson._id).depth;
            
            if (genDiff === 1 && !isKawetoPartner(firstPerson, secondPerson)) {
              relationship = 'CHILD';
            } else if (genDiff === 2) {
              relationship = 'GRANDCHILD';
            } else if (genDiff > 2) {
              relationship = `${getGreatPrefix(genDiff)}GRANDCHILD`;
            }
          } else if (secondDescendants.has(firstPerson._id)) {
            // firstPerson is a descendant of secondPerson
            // This means: firstPerson is CHILD/GRANDCHILD of secondPerson
            // We SWAP to display: firstPerson is GRANDCHILD of secondPerson
            const genDiff = secondDescendants.get(firstPerson._id).depth;
            
            if (genDiff === 1 && !isKawetoPartner(secondPerson, firstPerson)) {
              relationship = 'CHILD';
              isSwapped = true;
            } else if (genDiff === 2) {
              relationship = 'GRANDCHILD';
              isSwapped = true;
            } else if (genDiff > 2) {
              relationship = `${getGreatPrefix(genDiff)}GRANDCHILD`;
              isSwapped = true;
            }
          } else {
            const firstAncestorIds = new Set(firstAncestors.keys());
            const secondAncestorIds = new Set(secondAncestors.keys());
            let commonAncestor = null;
            let firstDepth = 0;
            let secondDepth = 0;

            for (const ancestorId of firstAncestorIds) {
              if (secondAncestorIds.has(ancestorId)) {
                commonAncestor = personMap[ancestorId];
                firstDepth = firstAncestors.get(ancestorId).depth;
                secondDepth = secondAncestors.get(ancestorId).depth;
                break;
              }
            }

            if (commonAncestor) {
              const genDiff = Math.abs(firstDepth - secondDepth);
              if (firstDepth === 1 && secondDepth === 1 && areSiblings(firstPerson, secondPerson, personMap)) {
                relationship = 'SIBLING';
              } else if (firstDepth > 0 && secondDepth > 0 && genDiff === 0) {
                const cousinLevel = Math.min(firstDepth, secondDepth) - 1;
                relationship = cousinLevel === 1 ? '1st COUSIN' : `${cousinLevel}th COUSIN`;
              } else if (genDiff === 1) {
                const olderPerson = firstDepth < secondDepth ? firstPerson : secondPerson;
                const youngerPerson = firstDepth < secondDepth ? secondPerson : firstPerson;
                isSwapped = firstDepth > secondDepth;

                const youngerParents = [youngerPerson.mother, youngerPerson.father, youngerPerson.socialParent]
                  .filter(id => id && personMap[id._id || id])
                  .map(id => personMap[id._id || id]);
                const isUncleOrAunt = youngerParents.some(parent => {
                  const isSibling = areSiblings(parent, olderPerson, personMap) || isParentSibling(olderPerson, parent, personMap);
                  const parentAncestors = getAncestorsWithDepth(parent, personMap);
                  const olderAncestors = getAncestorsWithDepth(olderPerson, personMap);
                  const parentGrandparents = Array.from(parentAncestors.entries())
                    .filter(([_, data]) => data.depth === 2)
                    .map(([id]) => id);
                  const olderGrandparents = Array.from(olderAncestors.entries())
                    .filter(([_, data]) => data.depth === 2)
                    .map(([id]) => id);
                  const sharesGrandparent = parentGrandparents.some(gp => olderGrandparents.includes(gp));
                  const isParent = [youngerPerson.mother, youngerPerson.father, youngerPerson.socialParent]
                    .some(id => id?._id === olderPerson._id);
                  return (isSibling || (sharesGrandparent && !isSibling)) && !isParent;
                });

                // Check if olderPerson is a cousin of youngerPerson's parent
                const isUncleOrAuntViaCousin = youngerParents.some(parent => areCousins(olderPerson, parent, personMap));

                if (isUncleOrAunt || isUncleOrAuntViaCousin) {
                  relationship = olderPerson.gender === 'MALE' ? 'UNCLE' : 'AUNT';
                } else {
                  relationship = youngerPerson.gender === 'MALE' ? 'NEPHEW' : 'NIECE';
                }
              } else if (genDiff === 2) {
                // FIXED: Distinguish between GRANDPARENT and GRAND_UNCLE/GRAND_AUNT
                const olderPerson = firstDepth < secondDepth ? firstPerson : secondPerson;
                const youngerPerson = firstDepth < secondDepth ? secondPerson : firstPerson;
                
                // Check if olderPerson is a DIRECT ancestor of youngerPerson
                const youngerAncestors = getAncestorsWithDepth(youngerPerson, personMap);
                const isDirectAncestor = youngerAncestors.has(olderPerson._id);
                
                if (isDirectAncestor) {
                  // Direct grandparent
                  relationship = 'GRANDPARENT';
                  isSwapped = firstDepth > secondDepth;
                } else {
                  // Older person is sibling of younger's grandparent = GRAND_UNCLE/GRAND_AUNT
                  const grandparents = Array.from(youngerAncestors.entries())
                    .filter(([_, data]) => data.depth === 2)
                    .map(([id]) => personMap[id])
                    .filter(Boolean);
                  
                  const isGrandparentSibling = grandparents.some(gp => areSiblings(olderPerson, gp, personMap));
                  
                  if (isGrandparentSibling) {
                    relationship = olderPerson.gender === 'MALE' ? 'GRAND_UNCLE' : 'GRAND_AUNT';
                    isSwapped = firstDepth > secondDepth;
                  } else {
                    // Fallback to grandparent
                    relationship = 'GRANDPARENT';
                    isSwapped = firstDepth > secondDepth;
                  }
                }
              } else if (genDiff === 3) {
                // Great-grandparent (including great-grand-uncles, etc.)
                relationship = 'GREAT_GRANDPARENT';
                isSwapped = firstDepth > secondDepth;
              } else if (genDiff === 4) {
                // Great-great-grandparent
                relationship = 'GREAT_GREAT_GRANDPARENT';
                isSwapped = firstDepth > secondDepth;
              } else if (genDiff > 4 && Math.min(firstDepth, secondDepth) === 0) {
                // For very large generation gaps (5+) where one person is at depth 0 (sibling of ancestor)
                // Treat as great-great-great...-grandparent
                const greats = 'GREAT_'.repeat(genDiff - 2);
                relationship = `${greats}GRANDPARENT`;
                isSwapped = firstDepth > secondDepth;
              } else if (genDiff > 2) {
                const cousinLevel = Math.min(firstDepth, secondDepth) - 1;
                const removed = Math.abs(firstDepth - secondDepth);
                relationship = `${cousinLevel}th COUSIN ${removed} TIME${removed > 1 ? 'S' : ''} REMOVED`;
                isSwapped = firstDepth < secondDepth;
              }
            } else {
              // No common ancestor found - check for special cases before defaulting to DISTANT_RELATIVE
              
              // Check if one person is a spouse of someone who IS related (grandparent-by-marriage)
              // This handles cases like: Susan (spouse of grand-uncle) → Robert (grand-nephew)
              
              
              // Check if firstPerson's spouse has a relationship to secondPerson
              const firstSpouses = (firstPerson.spouses || [])
                .filter(s => s && s._id && personMap[s._id])
                .map(s => personMap[s._id]);
              
              
              for (const spouse of firstSpouses) {
                
                // Get spouse's ancestors and secondPerson's ancestors to find common ancestor
                const spouseAncestors = getAncestorsWithDepth(spouse, personMap);
                const spouseAncestorIds = new Set(spouseAncestors.keys());
                
                
                // Find common ancestor between spouse and secondPerson
                let foundRelationship = false;
                for (const ancestorId of spouseAncestorIds) {
                  if (secondAncestors.has(ancestorId)) {
                    const spouseDepth = spouseAncestors.get(ancestorId).depth;
                    const secondDepth = secondAncestors.get(ancestorId).depth;
                    const genDiff = Math.abs(spouseDepth - secondDepth);
                    
                    
                    if (genDiff === 2) {
                      relationship = 'GRANDPARENT';
                      isSwapped = false;
                      foundRelationship = true;
                      break;
                    } else if (genDiff === 3) {
                      relationship = 'GREAT_GRANDPARENT';
                      isSwapped = false;
                      foundRelationship = true;
                      break;
                    } else if (genDiff === 4) {
                      relationship = 'GREAT_GREAT_GRANDPARENT';
                      isSwapped = false;
                      foundRelationship = true;
                      break;
                    } else if (genDiff > 4) {
                      const greats = 'GREAT_'.repeat(genDiff - 2);
                      relationship = `${greats}GRANDPARENT`;
                      isSwapped = false;
                      foundRelationship = true;
                      break;
                    }
                  }
                }
                
                if (foundRelationship) break;
                
                if (!foundRelationship) {
                }
              }
              
              // Check if secondPerson's spouse has a relationship to firstPerson
              if (relationship === 'NO_RELATION') {
                const secondSpouses = (secondPerson.spouses || [])
                  .filter(s => s && s._id && personMap[s._id])
                  .map(s => personMap[s._id]);
                
                
                for (const spouse of secondSpouses) {
                  
                  // Get spouse's ancestors and firstPerson's ancestors to find common ancestor
                  const spouseAncestors = getAncestorsWithDepth(spouse, personMap);
                  const spouseAncestorIds = new Set(spouseAncestors.keys());
                  
                  
                  // Find common ancestor between spouse and firstPerson
                  let foundRelationship = false;
                  for (const ancestorId of spouseAncestorIds) {
                    if (firstAncestors.has(ancestorId)) {
                      const spouseDepth = spouseAncestors.get(ancestorId).depth;
                      const firstDepth = firstAncestors.get(ancestorId).depth;
                      const genDiff = Math.abs(spouseDepth - firstDepth);
                      
                      
                      if (genDiff === 2) {
                        relationship = 'GRANDPARENT';
                        isSwapped = true;
                        foundRelationship = true;
                        break;
                      } else if (genDiff === 3) {
                        relationship = 'GREAT_GRANDPARENT';
                        isSwapped = true;
                        foundRelationship = true;
                        break;
                      } else if (genDiff === 4) {
                        relationship = 'GREAT_GREAT_GRANDPARENT';
                        isSwapped = true;
                        foundRelationship = true;
                        break;
                      } else if (genDiff > 4) {
                        const greats = 'GREAT_'.repeat(genDiff - 2);
                        relationship = `${greats}GRANDPARENT`;
                        isSwapped = true;
                        foundRelationship = true;
                        break;
                      }
                    }
                  }
                  
                  if (foundRelationship) break;
                  
                  if (!foundRelationship) {
                  }
                }
              }
              
              // If still no relationship found, mark as distant relative
              if (relationship === 'NO_RELATION') {
                relationship = 'DISTANT_RELATIVE';
              }
            }
          }
        }
      }
    }

    
    // BIDIRECTIONAL FIX: If no relationship found and this is the first call (depth = 0),
    // try the reverse direction to ensure we catch relationships regardless of order
    if (relationship === 'NO_RELATION' && depth === 0) {
      
      // Try the reverse direction
      const reverseResult = handleRelationship(
        { firstPersonId: secondPersonId, secondPersonId: firstPersonId }, 
        1  // depth = 1 to prevent infinite recursion
      );
      
      if (reverseResult && reverseResult.relationship && reverseResult.relationship !== 'NO_RELATION') {
        // Return the result from reverse call - it already set the relationshipResult correctly
        return reverseResult;
      }
    }


    const result = {
      firstPerson: isSwapped ? secondPerson.name : firstPerson.name,
      secondPerson: isSwapped ? firstPerson.name : secondPerson.name,
      relationship,
      isSwapped
    };


    setRelationshipResult(result);
    return result;
  };

  const handleTreeSearch = e => {
    const term = e.target.value;
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredPeople(people);
      setSubTreePerson(null); // Reset to show full tree
    } else {
      const filtered = people.filter(person => person.name.toLowerCase().includes(term.toLowerCase()));
      setFilteredPeople(people); // Keep full data so tree relationships work
      
      // If we found exactly one match, set it as the selected person to show their tree
      if (filtered.length === 1) {
        setSubTreePerson(filtered[0]);
      } else if (filtered.length > 1) {
        // If multiple matches, find exact match first
        const exactMatch = filtered.find(p => p.name.toLowerCase() === term.toLowerCase());
        if (exactMatch) {
          setSubTreePerson(exactMatch);
        } else {
          // Use the first match
          setSubTreePerson(filtered[0]);
        }
      } else {
        // No matches - keep current view
        setSubTreePerson(null);
      }
    }
  };

  const handleDelete = person => {
    setPendingDeletion(person);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeletion) return;
    const id = pendingDeletion._id;
    const apiUrl = process.env.REACT_APP_API_URL || "https://mwendwa-kitavi-family-tree-backend-production.up.railway.app";
    setLoading(true);
    try {
      await axios.delete(`${apiUrl}/api/people/${id}`);
      const res = await axios.get(`${apiUrl}/api/people`);
      const validPeople = res.data.filter(person => person && person._id && person.name);
      setPeople(validPeople);
      setFilteredPeople(validPeople);
      alert(`Successfully deleted ${pendingDeletion.name}`);
      if (selectedPerson?._id === id) setSelectedPerson(null);
    } catch (err) {
      console.error('Error deleting person:', err);
      await fetchPeople();
      alert('Failed to delete person. Data has been refreshed.');
    } finally {
      setOpenDeleteDialog(false);
      setPendingDeletion(null);
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setPendingDeletion(null);
  };

  const handleEdit = person => {
    setPersonToEdit(person);
    setShowEditForm(true);
    setShowAddForm(false);
    setView(null);
  };

  const handleSelect = person => {
    setSelectedPerson(person);
  };

  const handlePersonClick = person => {
    setSubTreePerson(person);
  };

  const handleBack = () => {
    setSubTreePerson(null);
    setFilteredPeople(people);
    setSearchTerm('');
  };

  const handleViewChange = newView => {
    // The view lives in the URL (/tree?view=flowchart) so the phone's back
    // button steps view -> tree page -> home, like a native app.
    setSearchParams({ view: newView });
    if ((newView === 'flowchart' || newView === 'flowchart2') && (!filteredPeople?.length)) {
      setFilteredPeople(people);
      setSubTreePerson(null);
    }
  };

  const renderTreeView = () => {
    if (view === 'flowchart') {
      return (
        <FlowChartView
          key={subTreePerson?._id || 'root'}
          data={filteredPeople}
          onPersonClick={handlePersonClick}
          onBack={handleBack}
          selectedPerson={subTreePerson}
          isMobile={isMobile}
        />
      );
    } else if (view === 'flowchart2') {
      return (
        <FlowChartView2
          key={subTreePerson?._id || 'root2'}
          data={filteredPeople}
          onPersonClick={personId => {
            setSubTreePerson(personId);
          }}
          onBack={handleBack}
          selectedPerson={subTreePerson}
          isMobile={isMobile}
        />
      );
    }
    return null;
  };

  // Original rendering logic for the app

  // Keep the rendered view in sync with the URL (?view=...), so the browser
  // and phone back button navigate between views naturally
  const viewParam = searchParams.get('view');
  useEffect(() => {
    const allowed = ['flowchart', 'flowchart2', 'network', 'table'];
    const next = allowed.includes(viewParam) ? viewParam : null;
    setView(next);
    if (!next) {
      setSelectedPerson(null);
      setSubTreePerson(null);
      setFilteredPeople(people);
      setSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewParam, people]);

  // Deep link from profile pages: /tree?rel1=<personId>&rel2=<personId>
  useEffect(() => {
    if (relDeepLinkRan.current || loading || !people.length) return;
    const rel1 = searchParams.get('rel1');
    const rel2 = searchParams.get('rel2');
    if (rel1 && rel2) {
      relDeepLinkRan.current = true;
      handleRelationship({ firstPersonId: rel1, secondPersonId: rel2 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, people, searchParams]);


  const relationPhrase = (rel) => {
    if (!rel) return 'related to';
    const specials = {
      SPOUSE: 'the spouse of', KAWETO: 'a kaweto partner of',
      PARENT: 'a parent of', CHILD: 'a child of',
      UNCLE: 'an uncle of', AUNT: 'an aunt of',
      NEPHEW: 'a nephew of', NIECE: 'a niece of',
      GRAND_UNCLE: 'a grand-uncle of', GRAND_AUNT: 'a grand-aunt of',
      FATHER_IN_LAW: 'a father-in-law of', MOTHER_IN_LAW: 'a mother-in-law of',
      SISTER_IN_LAW: 'a sister-in-law of', BROTHER_IN_LAW: 'a brother-in-law of',
      STEPMOTHER: 'the stepmother of', STEPFATHER: 'the stepfather of',
      NO_RELATION: 'not related to', DISTANT_RELATIVE: 'a distant relative of',
    };
    return specials[rel] || `a ${rel.toLowerCase().replace(/_/g, ' ')} of`;
  };

  const VIEW_TITLES = { flowchart: 'Flow Chart', flowchart2: 'Flow Chart 2', network: 'Fan Chart', table: 'Members Table' };

  const closeView = () => {
    // Same motion as the phone's back gesture: pop the ?view= history entry.
    // The URL-sync effect above handles all the state cleanup.
    if (searchParams.get('view')) {
      window.history.back();
    } else {
      setView(null);
    }
  };

  const viewCards = [
    { key: 'flowchart', title: 'Flow Chart', desc: 'Expand the family branch by branch.', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { key: 'flowchart2', title: 'Flow Chart 2', desc: 'The whole tree at a glance.', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { key: 'network', title: 'Fan Chart', desc: 'Generations radiating from the founder.', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { key: 'table', title: 'Members Table', desc: 'Every member, searchable and sortable.', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
  ];

  return (
    <Box sx={{ maxWidth: 1150, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Family Tree</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {people.length > 0 ? `${people.length} members — from Mwendwa Kitavi to today.` : 'From Mwendwa Kitavi to today.'}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!view && !loading && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {viewCards.map((c) => (
              <Grid item xs={6} sm={3} key={c.key}>
                <Card sx={{ borderRadius: 3, color: '#fff', background: c.gradient, height: '100%' }}>
                  <CardActionArea onClick={() => handleViewChange(c.key)} sx={{ height: '100%' }}>
                    <CardContent>
                      <AccountTreeIcon sx={{ fontSize: 32 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{c.title}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.95 }}>{c.desc}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {isEditor && (
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowAddForm(!showAddForm)}
              sx={{ mb: 3 }}
            >
              Add person to the tree
            </Button>
          )}

          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>How are we related?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Pick any two family members and let the tree explain the connection.
              </Typography>
              <SearchPerson people={people} onRelationship={handleRelationship} loading={loading} />
              {relationshipResult && (
                <Alert
                  severity={relationshipResult.relationship === 'NO_RELATION' ? 'info' : 'success'}
                  sx={{ mt: 2 }}
                  onClose={() => setRelationshipResult(null)}
                >
                  <b>{relationshipResult.firstPerson}</b> is {relationPhrase(relationshipResult.relationship)} <b>{relationshipResult.secondPerson}</b>.
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {view && !loading && (
        <Paper sx={{ borderRadius: 3, p: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button size="small" startIcon={<CloseIcon />} onClick={closeView}>
              All views
            </Button>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
              {VIEW_TITLES[view] || 'Tree view'}
            </Typography>
            {view !== 'table' && (
              <TextField
                label="Search by name"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleTreeSearch}
                sx={{ width: isMobile ? '100%' : 280 }}
              />
            )}
          </Box>

          <Box
            className="tree-view-container"
            sx={{
              width: '100%',
              height: view === 'table' ? 'auto' : (isMobile ? 'calc(100vh - 290px)' : 'calc(100vh - 250px)'),
              minHeight: view === 'table' ? 'auto' : 420,
              overflow: 'auto',
              borderRadius: 2,
              border: view === 'table' ? 'none' : '1px solid #eee',
              background: '#fff',
            }}
          >
            {view === 'table' ? (
              <FamilyMembersTable
                key={filteredPeople.length}
                people={filteredPeople}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSelect={(person) => navigate(`/person/${person._id}`)}
                onBack={closeView}
                isMobile={isMobile}
                canEdit={canEditTable}
              />
            ) : view === 'network' ? (
              <FanChartView
                data={people}
                selectedPerson={selectedPerson}
                onPersonClick={(person) => {
                  const personData = typeof person === 'object' && person._id ? person : people.find(p => p._id === person);
                  if (personData) setSelectedPerson(personData);
                }}
                onBack={closeView}
                isMobile={isMobile}
              />
            ) : (
              renderTreeView()
            )}
          </Box>

          {selectedPerson && !subTreePerson && view !== 'table' && (
            <Box sx={{ mt: 1.5, p: 1.5, background: '#f7f7fb', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedPerson.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPerson.gender || 'N/A'} • {selectedPerson.status || 'N/A'}
              </Typography>
              <Button size="small" sx={{ mt: 0.5 }} onClick={() => navigate(`/person/${selectedPerson._id}`)}>
                View profile →
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Add person */}
      <Dialog open={Boolean(showAddForm && !showEditForm)} onClose={() => { setShowAddForm(false); setSubTreePerson(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Add person to the tree</DialogTitle>
        <DialogContent>
          <PersonForm
            onUpdate={fetchPeople}
            people={people}
            onCancel={() => {
              setShowAddForm(false);
              setSubTreePerson(null);
            }}
            isMobile={isMobile}
          />
        </DialogContent>
      </Dialog>

      {/* Edit person */}
      <Dialog open={Boolean(showEditForm)} onClose={() => { setShowEditForm(false); setPersonToEdit(null); setSubTreePerson(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Edit person</DialogTitle>
        <DialogContent>
          <PersonForm
            onUpdate={fetchPeople}
            people={people}
            personToEdit={personToEdit}
            onCancel={() => {
              setShowEditForm(false);
              setPersonToEdit(null);
              setSubTreePerson(null);
            }}
            isMobile={isMobile}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title-unique"
        aria-describedby="delete-dialog-description"
        fullWidth
        maxWidth={isMobile ? 'xs' : 'sm'}
      >
        <DialogTitle id="delete-dialog-title-unique">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {pendingDeletion?.name || 'this person'}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">Cancel</Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TreePage;